
import { Component, Input, OnInit } from '@angular/core';
import Chart from 'chart.js/auto';
import { UnitSystem } from 'src/app/shared/enums/unit-system.enum';
import { UNITS } from 'src/app/shared/services/unit-conversion.service';
import { GeothermalService } from './services/geothermal.service';

@Component({
  selector: 'app-geothermal-results',
  templateUrl: './geothermal-results.component.html',
  styleUrls: ['./geothermal-results.component.css']
})

export class GeothermalResultsComponent {
  showThermalCollapse = false;
  showUnsupportedBuckling = false;

  // Units for Thermo-Collapse Model
  unitAxialStress = UNITS.FORCE_KIPS;
  unitApiCollapse = UNITS.PRESSURE_PSI;

  // Units for Unsupported Buckling Model
  unitCoeffUnsupportedLength = UNITS.LENGTH_FT;
  unitMaxCompStress = UNITS.PRESSURE_PSI;
  unitMaxDeltaT = UNITS.TEMPERATURE_F;
  unitUnsupportedLength = UNITS.LENGTH_FT;
  unitHolliday = '-';

  // Error properties - calculated once to avoid infinite change detection
  solutionErrors: { key: string, message: string }[] = [];
  unsupportedLengthInputErrors: { errorCode: number, errorMessage: string }[] = [];
  thermalCollapseInputErrors: { errorCode: number, errorMessage: string }[] = [];


  private _unitSystem: UnitSystem = UnitSystem.USC;

  constructor(private geothermalService: GeothermalService) {

  }

  updateUnitsBySystem(unit: UnitSystem) {
    if (unit === UnitSystem.USC) {
      this.unitAxialStress = UNITS.FORCE_KIPS;
      this.unitApiCollapse = UNITS.PRESSURE_PSI;
      this.unitCoeffUnsupportedLength = UNITS.LENGTH_FT;
      this.unitUnsupportedLength = UNITS.LENGTH_FT;
      this.unitMaxDeltaT = UNITS.TEMPERATURE_F;
      this.unitMaxCompStress = UNITS.PRESSURE_PSI;
    } else {
      this.unitAxialStress = UNITS.FORCE_KN;
      this.unitApiCollapse = UNITS.PRESSURE_KPA;
      this.unitCoeffUnsupportedLength = UNITS.LENGTH_M;
      this.unitUnsupportedLength = UNITS.LENGTH_M;
      this.unitMaxDeltaT = UNITS.TEMPERATURE_C;
      this.unitMaxCompStress = UNITS.PRESSURE_MPA;
    }
  }

  private _thermalCollapseResponses: any[] = [];
  @Input()
  set thermalCollapseResponses(val: any[]) {
    this._thermalCollapseResponses = val;
    this._unitSystem = this.geothermalService.getUnitSystem();
    this.updateUnitsBySystem(this._unitSystem);
    // Calculate errors once when data changes
    this.calculateThermalCollapseInputErrors();
    // Use longer delay to ensure canvas is properly sized, especially for loaded simulations
    setTimeout(() => this.initGraphThermoCollapseWithSizeCheck(), 100);
  }
  get thermalCollapseResponses(): any[] {
    return this._thermalCollapseResponses;
  }

  private _unsupportedLengthResponses: any = {};
  @Input()
  set unsupportedLengthResponses(val: any) {
    this._unsupportedLengthResponses = val;
    this._unitSystem = this.geothermalService.getUnitSystem();
    this.updateUnitsBySystem(this._unitSystem);
    // Calculate errors once when data changes
    this.calculateSolutionErrors();
    this.calculateUnsupportedLengthInputErrors();
    // Use longer delay to ensure canvas is properly sized, especially for loaded simulations
    setTimeout(() => this.initGraphUnsupportedBucklingWithSizeCheck(), 100);
  }
  get unsupportedLengthResponses(): any {
    return this._unsupportedLengthResponses;
  }

  private graphThermoCollapse: any = null;
  private graphUnsupportedBuckling: any = null;

  initGraphUnsupportedBuckling() {
    // Chart.js implementation
    if (this.graphUnsupportedBuckling) {
      this.graphUnsupportedBuckling.destroy();
      this.graphUnsupportedBuckling = null;
    }
    const dataArr = this.unsupportedLengthResponses?.outputs;
    if (!Array.isArray(dataArr) || !dataArr.length) return;

    const graphData = dataArr
      .filter(d => d.unsupportedBuckling != null && d.maxDeltaT != null)
      .map((d, index) => ({
        x: (d.unsupportedBuckling + 1).toFixed(3),
        y: d.maxDeltaT,
        index: index
      }));

    const ctx = (document.getElementById('graph-unsupported-buckling') as HTMLCanvasElement)?.getContext('2d');
    if (!ctx) return;


    const segment1 = graphData.slice(0, 8);
    const segment2 = graphData.slice(8);

    const maxY = Math.max(...graphData.map(d => d.y));

    let transitionSegment: any[] = [];
    if (segment1.length > 0 && segment2.length > 0) {
      transitionSegment = [
        { x: segment1[segment1.length - 1].x, y: maxY },
        { x: segment2[0].x, y: maxY }
      ];
    }

    this.graphUnsupportedBuckling = new Chart(ctx, {
      type: 'line',
      data: {
        labels: graphData.map(d => d.x),
        datasets: [
          {
            label: 'Max ΔT',
            data: segment1.map(d => d.y),
            borderColor: '#009000',
            backgroundColor: 'rgba(204,0,102,0.0)',
            fill: false,
            pointRadius: 3,
            pointBackgroundColor: '#009000',
            pointBorderColor: '#009000',
            tension: 0,
            spanGaps: false,
            pointStyle: 'circle'
          },
          {
            label: 'Max ΔT',
            data: [...Array(8).fill(null), ...segment2.map(d => d.y)],
            borderColor: '#009000',
            backgroundColor: 'rgba(204,0,102,0.0)',
            fill: false,
            pointRadius: 3,
            pointBackgroundColor: '#009000',
            pointBorderColor: '#009000',
            tension: 0,
            spanGaps: false,
            pointStyle: 'circle'

          },
          {
            label: 'Not Defined',
            data: [...Array(7).fill(null), ...transitionSegment.map(d => d.y), ...Array(graphData.length - 9).fill(null)],
            borderColor: 'rgba(128,128,128,0.3)',
            backgroundColor: 'rgba(128,128,128,0.3)',
            fill: true,
            pointRadius: 0,
            pointHoverRadius: 0,
            pointBackgroundColor: 'rgba(128,128,128,0.3)',
            pointBorderColor: 'rgba(128,128,128,0.3)',
            tension: 0,
            spanGaps: false,
          },
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            labels: {
              usePointStyle: true,
              boxWidth: 6,
              boxHeight: 6,
              filter: function (item: any) {
                return item.datasetIndex !== 1; // Hide duplicates
              }
            },
            onClick: (e) => { }
          },
          tooltip: {
            filter: function (item: any) {
              return item.datasetIndex !== 2; // Hide greyed transition segment && duplicates
            }
          }
        },
        elements: {
          line: { tension: 0 }
        },
        scales: {
          x: {
            type: 'linear',
            title: { display: true, text: `Unsupported Length [${this.unitUnsupportedLength}]` },
            ticks: {
              callback: function (value) {
                return Number(value).toFixed(0);
              }
            }
          },
          y: {
            title: { display: true, text: `Max ΔT [${this.unitMaxDeltaT}]` },
            beginAtZero: false
          }
        }
      }
    });
  }

  initGraphThermoCollapseWithSizeCheck() {
    const canvas = document.getElementById('graph-thermo-collapse') as HTMLCanvasElement;
    if (!canvas) return;
    
    // Check if canvas has proper size
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      // Canvas not properly sized yet, retry after another delay
      setTimeout(() => this.initGraphThermoCollapseWithSizeCheck(), 50);
      return;
    }
    
    this.initGraphThermoCollapse();
  }

  initGraphUnsupportedBucklingWithSizeCheck() {
    const canvas = document.getElementById('graph-unsupported-buckling') as HTMLCanvasElement;
    if (!canvas) return;
    
    // Check if canvas has proper size
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      // Canvas not properly sized yet, retry after another delay
      setTimeout(() => this.initGraphUnsupportedBucklingWithSizeCheck(), 50);
      return;
    }
    
    this.initGraphUnsupportedBuckling();
  }

  initGraphThermoCollapse() {
    // Chart.js implementation
    if (this.graphThermoCollapse) {
      this.graphThermoCollapse.destroy();
      this.graphThermoCollapse = null;
    }
    if (!this.thermalCollapseResponses || !this.thermalCollapseResponses.length) return;

    let maxRows = 0;
    for (const resp of this.thermalCollapseResponses) {
      const afLen = resp && resp.axialForce ? Object.keys(resp.axialForce).length : 0;
      const crLen = resp && resp.collapseRating ? Object.keys(resp.collapseRating).length : 0;
      maxRows = Math.max(maxRows, afLen, crLen);
    }

    const datasets: any[] = [];
    const colors = ['#009000', '#CC0066', '#000090', '#666666', '#FF9900'];
    const pointStyles = ['circle', 'rect', 'rectRounded', 'rectRot', 'triangle'];

    for (let t = 0; t < this.thermalCollapseResponses.length; t++) {
      const resp = this.thermalCollapseResponses[t];
      if (!resp) continue;
      const label = `T${t + 1}-${resp.temperature.toFixed(1)} ${this.unitMaxDeltaT}`;
      const data: { x: number, y: number }[] = [];
      for (let i = 0; i < maxRows; i++) {
        const x = resp.axialForce && resp.axialForce[i] !== undefined ? resp.axialForce[i] : null;
        const y = resp.collapseRating && resp.collapseRating[i] !== undefined ? resp.collapseRating[i] : null;
        if (x !== null && y !== null) {
          data.push({ x, y });
        }
      }
      if (data.length) {
        datasets.push({
          label,
          data: data.map(pt => ({ x: pt.x, y: pt.y })),
          borderColor: colors[t % colors.length],
          backgroundColor: colors[t % colors.length] + '33',
          fill: false,
          pointRadius: 3,
          pointStyle: pointStyles[t % pointStyles.length],
          pointBackgroundColor: colors[t % colors.length],
          pointBorderColor: colors[t % colors.length],
          tension: 0,
          spanGaps: false
        });
      }
    }

    const ctx = (document.getElementById('graph-thermo-collapse') as HTMLCanvasElement)?.getContext('2d');
    if (!ctx) return;

    this.graphThermoCollapse = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            labels: {
              usePointStyle: true,
              boxWidth: 6,
              boxHeight: 6,
            },
          }
        },
        elements: {
          line: { tension: 0 }
        },
        scales: {
          x: {
            type: 'linear',
            title: { display: true, text: `Axial Force [${this.unitAxialStress}]` },
            beginAtZero: false
          },
          y: {
            title: { display: true, text: `Collapse Rating [${this.unitApiCollapse}]` },
            beginAtZero: false
          }
        }
      }
    });
  }
  getThermalCollapseRowIndexes(): number[] {
    if (!this.thermalCollapseResponses || this.thermalCollapseResponses.length === 0) return [];

    let max = 0;
    for (const resp of this.thermalCollapseResponses) {
      const afLen = resp && resp.axialForce ? Object.keys(resp.axialForce).length : 0;
      const crLen = resp && resp.collapseRating ? Object.keys(resp.collapseRating).length : 0;
      max = Math.max(max, afLen, crLen);
    }
    return Array.from({ length: max }, (_, i) => i);
  }

  // Private methods to calculate errors once when data changes
  private calculateSolutionErrors(): void {
    if (!Array.isArray(this.unsupportedLengthResponses?.outputs)) {
      this.solutionErrors = [];
      return;
    }
    this.solutionErrors = this.unsupportedLengthResponses.outputs
      .map((out: any, i: number) => ({
        key: `L${i + 1}`,
        message: out?.solutionError?.errorMessage?.trim?.() || ''
      }))
      .filter(e => e.message);
  }

  private calculateUnsupportedLengthInputErrors(): void {
    this.unsupportedLengthInputErrors = Array.isArray(this.unsupportedLengthResponses?.inputsErrors) ?
      this.unsupportedLengthResponses.inputsErrors : [];
  }

  private calculateThermalCollapseInputErrors(): void {
    this.thermalCollapseInputErrors = [];
    if (!Array.isArray(this.thermalCollapseResponses)) return;

    this.thermalCollapseResponses.forEach((response: any) => {
      if (Array.isArray(response?.inputsErrors)) {
        this.thermalCollapseInputErrors.push(...response.inputsErrors);
      }
    });
  }

  // Public methods that return boolean values or reference properties (safe for templates)
  hasSolutionErrors(): boolean {
    return this.solutionErrors.length > 0;
  }

  getSolutionErrors(): { key: string, message: string }[] {
    return this.solutionErrors;
  }

  hasThermalCollapseInputErrors(): boolean {
    return this.thermalCollapseInputErrors.length > 0;
  }

  hasUnsupportedLengthInputErrors(): boolean {
    return this.unsupportedLengthInputErrors.length > 0;
  }

  getThermalCollapseInputErrors(): { errorCode: number, errorMessage: string }[] {
    return this.thermalCollapseInputErrors;
  }

  getUnsupportedLengthInputErrors(): { errorCode: number, errorMessage: string }[] {
    return this.unsupportedLengthInputErrors;
  }
}
